"use client";

import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

interface NavItem {
  label: string;
  href: string;
}

interface MiniNavbarProps {
  navItems?: NavItem[];
  onLoginClick?: () => void;
  onSignupClick?: () => void;
  showAuthButtons?: boolean;
  logoElement?: React.ReactNode;
}

const AnimatedNavLink = ({ href, children, onClick, currentPath }: { href: string; children: React.ReactNode; onClick?: () => void; currentPath: string }) => {
  const navigate = useNavigate();

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (href.startsWith('#')) {
      e.preventDefault();
      const element = document.querySelector(href);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (href.startsWith('/#')) {
      e.preventDefault();
      const hash = href.split('#')[1];
      if (currentPath === '/') {
        const element = document.querySelector(`#${hash}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      } else {
        navigate(href);
      }
    }
    onClick?.();
  };

  return (
    <Link 
      to={href.startsWith('#') ? currentPath + href : href} 
      onClick={handleClick} 
      className="group relative inline-block overflow-hidden h-6 text-xs sm:text-sm font-medium"
    >
      <div className="flex flex-col transition-transform duration-300 ease-out transform group-hover:-translate-y-1/2 h-12">
        <span className="font-medium h-6 flex items-center leading-6" style={{ color: "#1e0d01" }}>{children}</span>
        <span className="font-medium h-6 flex items-center leading-6" style={{ color: "hsl(var(--primary))" }}>{children}</span>
      </div>
    </Link>
  );
};

export function MiniNavbar({
  navItems = [
    { label: 'Features', href: '/features' },
    { label: 'How It Works', href: '/how-it-works' },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Contact', href: '/contact' },
  ],
  onLoginClick,
  onSignupClick,
  showAuthButtons = true,
  logoElement
}: MiniNavbarProps) {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const toggleMenu = () => {
    setIsOpen(prev => !prev);
  };

  const defaultLogoElement = (
    <div className="relative w-5 h-5 flex items-center justify-center cursor-pointer" onClick={() => navigate('/')}>
      <span className="absolute w-1.5 h-1.5 rounded-full bg-primary top-0 left-1/2 transform -translate-x-1/2 opacity-80"></span>
      <span className="absolute w-1.5 h-1.5 rounded-full bg-primary left-0 top-1/2 transform -translate-y-1/2 opacity-80"></span>
      <span className="absolute w-1.5 h-1.5 rounded-full bg-primary right-0 top-1/2 transform -translate-y-1/2 opacity-80"></span>
      <span className="absolute w-1.5 h-1.5 rounded-full bg-primary bottom-0 left-1/2 transform -translate-x-1/2 opacity-80"></span>
    </div>
  );

  const loginButtonElement = (
    <button 
      onClick={onLoginClick}
      className="px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm border border-[#1e0d01]/15 bg-white/80 rounded-full transition-colors duration-200 w-full md:w-auto backdrop-blur-sm whitespace-nowrap font-medium"
      style={{ color: "rgba(30,13,1,0.7)" }}
    >
      Sign In
    </button>
  );

  const signupButtonElement = (
    <div className="relative group w-full md:w-auto">
       <div className="absolute inset-0 -m-2 rounded-full
                     hidden md:block
                     bg-primary
                     opacity-30 filter blur-lg pointer-events-none
                     transition-all duration-300 ease-out
                     group-hover:opacity-50 group-hover:blur-xl group-hover:-m-3"></div>
       <button 
         onClick={onSignupClick}
         className="relative z-10 px-3 py-1.5 sm:px-4 sm:py-2 text-xs sm:text-sm font-semibold text-white rounded-full transition-all duration-200 w-full md:w-auto whitespace-nowrap hover:scale-105 active:scale-95"
         style={{ background: "hsl(var(--primary))", boxShadow: "0 10px 20px -5px hsl(var(--primary) / 0.4)" }}
       >
         Get Started
       </button>
    </div>
  );

  return (
    <header className={`fixed top-4 sm:top-6 z-50
                       left-2 right-2 sm:left-4 sm:right-4 md:left-1/2 md:right-auto md:w-auto md:-translate-x-1/2
                       flex flex-col items-center
                       pl-4 pr-4 sm:pl-6 sm:pr-6 py-2.5 sm:py-3 backdrop-blur-md
                       ${isOpen ? 'rounded-xl' : 'rounded-full'}
                       border border-gray-200 bg-white/95
                       shadow-lg`}>

      <div className="flex items-center justify-between w-full gap-x-4 sm:gap-x-6 md:gap-x-8">
        <div className="flex items-center flex-shrink-0">
           {logoElement || defaultLogoElement}
        </div>

        <nav className="hidden md:flex items-center space-x-3 lg:space-x-4 xl:space-x-6 h-6">
          {navItems.map((link) => (
            <AnimatedNavLink key={link.href} href={link.href} onClick={() => setIsOpen(false)} currentPath={location.pathname}>
              {link.label}
            </AnimatedNavLink>
          ))}
        </nav>

        {showAuthButtons && (
          <div className="hidden md:flex items-center gap-2 lg:gap-3">
            {loginButtonElement}
            {signupButtonElement}
          </div>
        )}

        <button className="md:hidden flex items-center justify-center w-8 h-8 sm:w-9 sm:h-9 focus:outline-none flex-shrink-0" style={{ color: "#1e0d01" }} onClick={toggleMenu} aria-label={isOpen ? 'Close Menu' : 'Open Menu'}>
          {isOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
          ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"></path></svg>
          )}
        </button>
      </div>

      {isOpen && (
        <div className="md:hidden flex flex-col items-center w-full pt-4 pb-2">
          <nav className="flex flex-col items-center space-y-3 sm:space-y-4 text-sm sm:text-base w-full px-2">
            {navItems.map((link) => (
              <Link
                key={link.href}
                to={link.href.startsWith('#') ? location.pathname + link.href : link.href}
                onClick={() => {
                  setIsOpen(false);
                  if (link.href.startsWith('#')) {
                    setTimeout(() => {
                      const element = document.querySelector(link.href);
                      if (element) {
                        element.scrollIntoView({ behavior: 'smooth' });
                      }
                    }, 100);
                  }
                }}
                className="transition-opacity hover:opacity-60 w-full text-center font-medium py-1"
                style={{ color: "#1e0d01" }}
              >
                {link.label}
              </Link>
            ))}
          </nav>
          {showAuthButtons && (
            <div className="flex flex-col items-center space-y-3 sm:space-y-4 mt-3 sm:mt-4 w-full px-4">
              {loginButtonElement}
              {signupButtonElement}
            </div>
          )}
        </div>
      )}
    </header>
  );
}
